import type { Api } from "../../data/api";
import { buildView, outcomeFromError, outcomeFromResponse, tokenLooksValid, type InsightsView, type ShareOutcome, type ShareResponse } from "./insights-model";

/**
 * Öffnet eine Insights-Freigabe als Marke (anonym, auch wenn im Browser jemand angemeldet ist).
 * ig_share_open schreibt (Fehlversuche, Aufrufprotokoll) und darf deshalb nur auf ausdrückliche Eingabe laufen, nie in einer
 * Abfrage, die sich nach jeder Änderung neu lädt: Jeder Aufruf zählt als Aufruf oder als Fehlversuch der Marke.
 * Die Bilder (privater Bucket ig-media, 0429) kommen als signierte Adressen in media_urls mit; buildView nimmt sie von dort.
 */
export async function openShare(api: Pick<Api, "rpcAsAnon" | "igShareOpen">, token: string, email: string): Promise<ShareOutcome> {
  if (!tokenLooksValid(token)) return { kind: "invalid" };
  const clean = email.trim().toLowerCase();
  try {
    // 0429: mit Supabase über die Serverfunktion ig-share (gleiche Prüfung, dazu signierte Adressen der Bilder); Demo: direkt
    const r = api.igShareOpen ? await api.igShareOpen(token, clean) : await api.rpcAsAnon<unknown>("ig_share_open", { p_token: token, p_email: clean });
    return outcomeFromResponse(r, clean);
  } catch (e) {
    return outcomeFromError(e);
  }
}

export type TeamOutcome =
  | { kind: "team"; view: InsightsView; locked: boolean; recipient: string | null; raw?: ShareResponse }
  | { kind: "revoked" }
  | { kind: "expired" }
  | { kind: "none" };

/**
 * Angemeldete Mitarbeiter der Agentur sehen die Freigabe so, wie die Marke sie sieht: ohne Mail-Prüfung, ohne Fehlversuch und
 * ohne dass der Aufruf als Aufruf der Marke zählt (ig_share_open_team, 0042; liest nur). Gehört der Link nicht zur eigenen
 * Agentur oder ist niemand angemeldet, bleibt es bei der Prüfung für die Marke.
 */
export async function openShareAsTeam(api: Pick<Api, "read">, token: string): Promise<TeamOutcome> {
  if (!tokenLooksValid(token)) return { kind: "none" };
  try {
    const r = await api.read<unknown>("ig_share_open_team", { p_token: token });
    const o = (typeof r === "string" ? JSON.parse(r) : r) as (ShareResponse & { locked?: boolean; recipient?: string | null }) | null;
    if (!o || o.ok !== true) return { kind: "none" };
    const recipient = typeof o.recipient === "string" ? o.recipient : null;
    return { kind: "team", view: buildView(o, recipient ?? "die Marke"), locked: o.locked === true, recipient, raw: o };
  } catch (e) {
    const out = outcomeFromError(e);
    return out.kind === "revoked" || out.kind === "expired" ? out : { kind: "none" };
  }
}
